import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { GraduationCap, ArrowRight, CheckCircle2, Users, BookOpen, Globe, Award, BarChart3, Lightbulb } from "lucide-react";
import { SectionHeader } from "@/components/SectionComponents";

const features = [
  { icon: Users,     title: "Student Language Exchange",  description: "Pair students with native-speaking peers worldwide for authentic, curriculum-aligned conversation practice." },
  { icon: BookOpen,  title: "Faculty Development",       description: "Help professors and staff improve language skills to support international research collaboration." },
  { icon: Globe,     title: "International Programs",    description: "Prepare students for exchange semesters and international internships with targeted language preparation." },
  { icon: Award,     title: "Accreditation Support",     description: "Generate documented language progress reports that support institutional accreditation requirements." },
  { icon: BarChart3, title: "Academic Analytics",       description: "Track cohort-level language progress, participation rates, and outcomes aligned with your academic KPIs." },
  { icon: Lightbulb, title: "Research Partnerships",    description: "Access anonymized language learning data for linguistics, education, and AI research programs." },
];

export default function Universities() {
  return (
    <div className="min-h-screen bg-background">

      {/* Hero */}
      <section className="pt-28 pb-20 bg-surface border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-primary mb-4">For Universities</p>
              <h1 className="text-4xl sm:text-5xl font-display font-bold text-foreground leading-tight mb-5">
                Elevate Academic Language Programs
              </h1>
              <p className="text-base text-muted-foreground leading-relaxed mb-8">
                Lingua2 gives universities a scalable, measurable platform for student language development — AI precision with authentic human exchange.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button asChild className="bg-primary hover:bg-primary-dark text-primary-foreground shadow-primary font-semibold transition-fast">
                  <Link to="/contact">Get University Pricing <ArrowRight className="ml-2 w-4 h-4" /></Link>
                </Button>
                <Button asChild variant="outline" className="transition-fast">
                  <Link to="/how-it-works">How It Works</Link>
                </Button>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl-token">
              <img
                src="https://images.unsplash.com/photo-1721702754494-fdd7189f946c?crop=entropy&cs=srgb&fm=jpg&q=85&w=800"
                alt="University students studying"
                className="w-full h-[360px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader eyebrow="Built for Higher Education" title="Designed Around How Universities Work" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {features.map((f) => (
              <div key={f.title} className="group p-6 rounded-xl border border-border bg-card card-hover hover:border-primary/25">
                <div className="w-10 h-10 rounded-lg bg-primary/8 flex items-center justify-center mb-4 group-hover:bg-primary/14 transition-fast">
                  <f.icon className="w-5 h-5 text-primary" strokeWidth={1.75} />
                </div>
                <h3 className="text-sm font-display font-semibold text-foreground mb-2">{f.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.description}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Button asChild className="bg-primary hover:bg-primary-dark text-primary-foreground shadow-primary transition-fast">
              <Link to="/contact">Contact Our Academic Team <ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="py-16 bg-primary">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            {[
              { value: "40%",  label: "Avg. Language Improvement" },
              { value: "85%",  label: "Student Engagement Rate" },
              { value: "120+", label: "University Partners" },
              { value: "60+",  label: "Countries Represented" },
            ].map((s) => (
              <div key={s.label}>
                <div className="text-3xl font-display font-bold text-primary-foreground mb-1">{s.value}</div>
                <div className="text-sm text-primary-foreground/70">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Checklist */}
      <section className="py-20 bg-surface">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader eyebrow="Included" title="What Universities Get" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              "Dedicated academic success manager",
              "LMS integration (Moodle, Canvas, Blackboard)",
              "Multilingual platform interface",
              "FERPA/GDPR compliant data handling",
              "Curriculum alignment tools",
              "Annual academic impact report",
              "Faculty training and onboarding",
              "Batch enrollment for entire cohorts",
            ].map((item) => (
              <div key={item} className="flex items-center gap-3 p-4 rounded-lg border border-border bg-card">
                <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                <span className="text-sm text-foreground">{item}</span>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Button size="lg" asChild className="bg-primary hover:bg-primary-dark text-primary-foreground shadow-primary transition-fast">
              <Link to="/contact">Request a Proposal <ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
